var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Banner, Button, ButtonList, Column, Columns, DropdownButton, DropdownButtonContext, EmptyState, FlexBox, Icon, Spinner, Tabs, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { colors, typography } from '@dixa/lib-shared-tokens';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSetState } from 'react-use';
import { flowsApi } from 'apis/flows';
import { analytics } from 'app/utils/segment';
import { ReviewConflictsModal } from 'components/settings/components/sections/openingHours/components/reviewConflictsModal/reviewConflictsModal';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useNavigatePrompt } from 'utils/url/useNavigatePrompt';
import { RenameModal } from '../../../../components/renameModal/renameModal';
import { ListItemType, useScheduleContext, } from '../../schedule.context';
import { ImportHolidaysModal } from './components/ImportHolidaysModal/ImportHolidaysModal';
import { BusinessHoursList } from './components/businessHoursList/businessHoursList';
import { ExceptionsList } from './components/exceptionsList/exceptionsList';
import { Preview } from './components/preview/preview';
import { RecoverHolidaysModal } from './components/recoverHolidaysModal/recoverHolidaysModal';
import { TimezoneModal } from './components/timezoneModal/timezoneModal';
import { getDeletedHolidays } from './utils/getDeletedHolidays';
const viewTabs = {
    businessHours: 'Business hours',
    exceptions: 'Exceptions',
};
function hasErrorIntervalsError(errors) {
    return errors.flat().some((error) => error === null || error === void 0 ? void 0 : error.length);
}
export const ScheduleItem = () => {
    const [{ audioMessage, countryCode, name, timezone }, setState] = useSetState({
        audioMessage: {
            url: '',
            label: '',
            author: '',
        },
        countryCode: null,
        name: '',
        timezone: '',
    });
    const [activeModalType, setActiveModalType] = useState(null);
    const notify = useNotifications();
    const { activeView, autoReply, conflicts, holidays, initialSchedule, intervalsErrors, listItems: { [ListItemType.Exception]: exceptions }, meta: { isError }, modifiedListItems, refetchSchedules, setActiveView, setAutoReply, setInitialState, setModifiedListItems, } = useScheduleContext();
    const { id } = useAppPathParams('/settings/businesshours/:id');
    const agentName = useAppSelector((state) => state.settings.profile.profile.name);
    const [updateSchedule, { isLoading }] = flowsApi.useUpdateFlowScheduleMutation();
    const [readCountry, { data: country }] = flowsApi.useLazyReadFlowsSchedulesCountryQuery();
    useEffect(() => {
        if (initialSchedule) {
            setState({
                audioMessage: initialSchedule.announcement,
                countryCode: initialSchedule.country_code,
                name: initialSchedule.name,
                timezone: initialSchedule.timezone,
            });
        }
    }, [initialSchedule, setState]);
    useEffect(() => {
        if (countryCode) {
            readCountry({ country_code: countryCode });
        }
    }, [countryCode, readCountry]);
    const deletedHolidays = useMemo(() => getDeletedHolidays({ agentName, exceptions, holidays }), [agentName, exceptions, holidays]);
    const conflictedDates = [
        ...new Set(Object.values(conflicts).map(({ next_date }) => next_date)),
    ];
    const handleSetActiveView = useCallback((key, type) => {
        if (key === 'tab') {
            analytics.businessHoursScheduleTabClicked({ view: type });
        }
        if (activeView[key] !== type) {
            setActiveView(Object.assign(Object.assign(Object.assign({}, activeView), { [key]: type }), (key === 'tab' && { menu: 'hours' })));
        }
    }, [activeView, setActiveView]);
    const handleSetActiveModalType = useCallback((type, toggleDropdown) => () => {
        if (!type) {
            setActiveModalType(null);
            return;
        }
        switch (type) {
            case 'import':
                analytics.businessHoursScheduleOptionsMenuImportHolidaysClicked();
                break;
            case 'recover':
                analytics.businessHoursScheduleOptionsMenuRecoverHolidaysClicked();
                break;
            case 'rename':
                analytics.businessHoursScheduleOptionsMenuRenameClicked();
                break;
            case 'timezone':
                analytics.businessHoursScheduleOptionsMenuTimezoneClicked();
                break;
        }
        setActiveModalType(type);
        toggleDropdown === null || toggleDropdown === void 0 ? void 0 : toggleDropdown();
    }, []);
    const handleAddHolidays = useCallback((modifiedListItems, countryCode) => {
        setModifiedListItems(ListItemType.Exception, modifiedListItems);
        if (countryCode) {
            setState({ countryCode });
        }
    }, [setModifiedListItems, setState]);
    const handleResolveConflicts = useCallback((modifiedItems) => {
        setModifiedListItems(ListItemType.Exception, modifiedItems);
    }, [setModifiedListItems]);
    const handleSaveSchedule = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        analytics.businessHoursScheduleSaveScheduleButtonClicked();
        const openingHours = Object.values(modifiedListItems).flatMap((listItems) => Object.values(listItems));
        if (initialSchedule) {
            const payload = Object.assign({ announcement: audioMessage, auto_reply_template_id: autoReply, country_code: countryCode, name,
                timezone, version: initialSchedule.version }, ((openingHours === null || openingHours === void 0 ? void 0 : openingHours.length) && { opening_hours: openingHours }));
            const res = yield updateSchedule({ id, schedule: payload });
            if ('data' in res) {
                notify({
                    title: 'Schedule updated!',
                    type: 'success',
                    time: 5000,
                }, NotificationType.Success);
                setInitialState();
                refetchSchedules();
            }
            else {
                notify({
                    title: `An error occurred when trying to update the schedule.`,
                }, NotificationType.Error);
            }
        }
    }), [
        audioMessage,
        autoReply,
        countryCode,
        id,
        initialSchedule,
        modifiedListItems,
        name,
        timezone,
        notify,
        refetchSchedules,
        setInitialState,
        updateSchedule,
    ]);
    const hasBusinessHoursIntervalsError = hasErrorIntervalsError(intervalsErrors.Standard);
    const hasExceptionsIntervalsError = hasErrorIntervalsError(intervalsErrors.Exception);
    const hasIntervalsError = hasBusinessHoursIntervalsError || hasExceptionsIntervalsError;
    const hasChanges = name !== (initialSchedule === null || initialSchedule === void 0 ? void 0 : initialSchedule.name) ||
        timezone !== (initialSchedule === null || initialSchedule === void 0 ? void 0 : initialSchedule.timezone) ||
        autoReply !== initialSchedule.auto_reply_template_id ||
        !isEqual(initialSchedule === null || initialSchedule === void 0 ? void 0 : initialSchedule.announcement, audioMessage) ||
        Object.values(modifiedListItems).some((modifiedItem) => Object.keys(modifiedItem).length);
    useNavigatePrompt(hasChanges);
    const hasConflict = !!Object.keys(conflicts).length;
    const isSaveDisabled = hasIntervalsError || !hasChanges || hasConflict;
    const getTooltipContent = () => {
        if (hasIntervalsError && hasConflict) {
            return 'You can’t save a schedule with conflicting time slots and conflicting dates';
        }
        return hasIntervalsError
            ? 'You can’t save a schedule with conflicting time slots'
            : 'You can’t save a schedule with conflicting dates';
    };
    const hasDeletedHolidays = !!deletedHolidays.length;
    const tabs = useMemo(() => Object.entries(viewTabs).map(([key, value]) => {
        const slotStart = (_jsx(Icon, { name: "exclamation-triangle-filled", color: colors.orange['800'] }));
        const shouldRenderSlotStart = (key === 'businessHours' && hasBusinessHoursIntervalsError) ||
            (key === 'exceptions' &&
                (hasExceptionsIntervalsError || hasConflict));
        return Object.assign({ id: key, label: value }, (shouldRenderSlotStart && { slotStart }));
    }), [hasBusinessHoursIntervalsError, hasConflict, hasExceptionsIntervalsError]);
    if (initialSchedule) {
        return (_jsxs(_Fragment, { children: [_jsx(Columns, { children: _jsxs(Column, { children: [_jsxs(Column.Row, Object.assign({ slottable: true }, { children: [_jsx(Column.Slot, Object.assign({ placement: "start" }, { children: _jsxs(FlexBox, Object.assign({ gap: "200", alignItems: "center" }, { children: [_jsx(Text.Heading, { children: name }), _jsx(Badge, Object.assign({ variant: "secondary", size: "medium" }, { children: `Used in ${initialSchedule.number_of_flows} ${initialSchedule.number_of_flows === 1 ? 'flow' : 'flows'}` }))] })) })), _jsx(Column.Slot, Object.assign({ placement: "end", noVerticalPadding: true }, { children: _jsxs(FlexBox, Object.assign({ alignItems: "center", gap: "200" }, { children: [_jsx(DropdownButton, { buttonProps: { variant: 'secondary' }, fixed: true, icon: "dots-v", noArrow: true, onToggle: (isOpen) => {
                                                        if (isOpen) {
                                                            analytics.businessHoursScheduleOptionsMenuOpened();
                                                        }
                                                    }, dropdown: _jsx(DropdownButtonContext.Consumer, { children: ({ toggleDropdown }) => (_jsxs(ButtonList, { children: [_jsx(ButtonList.Item, Object.assign({ onClick: handleSetActiveModalType('rename', toggleDropdown) }, { children: "Rename schedule" })), _jsx(ButtonList.Item, Object.assign({ onClick: handleSetActiveModalType('timezone', toggleDropdown) }, { children: "Edit timezone" })), _jsx(ButtonList.Item, Object.assign({ isDisabled: !!countryCode && !hasDeletedHolidays, onClick: handleSetActiveModalType(countryCode ? 'recover' : 'import', toggleDropdown) }, { children: countryCode
                                                                        ? 'Recover deleted holidays'
                                                                        : 'Connect holiday calendar' }))] })) }) }), _jsx(Button, Object.assign({ active: activeView.view === 'preview', onClick: () => {
                                                        analytics.businessHoursSchedulePreviewButtonClicked({
                                                            view: activeView.view.toLowerCase(),
                                                        });
                                                        handleSetActiveView('view', activeView.view === 'edit' ? 'preview' : 'edit');
                                                    }, variant: "secondary" }, { children: "Preview" })), _jsx(Tooltip, Object.assign({ content: getTooltipContent(), dark: true, disabled: !hasIntervalsError && !hasConflict, side: "left" }, { children: _jsx(Button, Object.assign({ disabled: isSaveDisabled, onClick: handleSaveSchedule, isLoading: isLoading }, { children: "Save changes" })) }))] })) }))] })), _jsx(Column.Row, Object.assign({ noVerticalPadding: true }, { children: _jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "200" }, { children: [_jsxs(FlexBox, Object.assign({ alignItems: "center", gap: "200" }, { children: [_jsx(Icon, { name: "clock-three" }), _jsx(Text.Paragraph, { children: timezone })] })), _jsxs(FlexBox, Object.assign({ alignItems: "center", gap: "200" }, { children: [_jsx(Icon, { name: "calendar" }), country ? (_jsx(Text.Paragraph, { children: country.name })) : (_jsx(Text.Paragraph, Object.assign({ color: typography.color.tertiary }, { children: "No holiday calendar imported" })))] }))] })) })), !!conflictedDates.length && (_jsx(Column.Row, Object.assign({ noMinHeight: true }, { children: _jsx(Banner, { variant: "warning", headline: `Please review ${conflictedDates.length} conflicting dates`, description: _jsxs(Text.Paragraph, Object.assign({ variant: "4" }, { children: ["Select which business hours apply on the conflicting dates for the schedule ", name, "."] })), actionSlot: _jsx(Button, Object.assign({ onClick: () => {
                                            analytics.businessHoursScheduleConflictReviewButtonClicked();
                                            setActiveModalType('review');
                                        }, variant: "secondary" }, { children: "Review conflicts" })) }) }))), activeView.view === 'edit' && (_jsxs(_Fragment, { children: [_jsx(Column.Row, Object.assign({ noVerticalPadding: true, noMinHeight: true, borderBottom: true }, { children: _jsx(Tabs, { onChange: (type) => handleSetActiveView('tab', type), selectedId: activeView.tab, tabs: tabs }) })), activeView.tab === 'businessHours' && (_jsx(BusinessHoursList, { audioMessage: audioMessage, autoReply: autoReply, onAudioMessageChange: (audioMessage) => void setState({ audioMessage }), onAutoReplyChange: (autoReply) => void setAutoReply(autoReply || null) })), activeView.tab === 'exceptions' && _jsx(ExceptionsList, {})] })), activeView.view === 'preview' && (_jsx(Column.Row, Object.assign({ size: 1 }, { children: _jsx(Preview, {}) })))] }) }), activeModalType === 'import' && (_jsx(ImportHolidaysModal, { onClose: handleSetActiveModalType(), onSubmit: handleAddHolidays, timezone: timezone })), activeModalType === 'recover' && country && (_jsx(RecoverHolidaysModal, { country: country, holidays: deletedHolidays, onClose: handleSetActiveModalType(), onSubmit: handleAddHolidays })), activeModalType === 'rename' && (_jsx(RenameModal, { mode: "edit", name: name, onClose: handleSetActiveModalType(), onSubmit: (name) => setState({ name }) })), activeModalType === 'review' && (_jsx(ReviewConflictsModal, { conflicts: conflicts, onClose: handleSetActiveModalType(), onResolve: handleResolveConflicts })), activeModalType === 'timezone' && (_jsx(TimezoneModal, { timezone: timezone, onClose: handleSetActiveModalType(), onSubmit: (timezone) => setState({ timezone }) }))] }));
    }
    return (_jsx(FlexBox, Object.assign({ flex: "1", justifyContent: "center", alignItems: "center" }, { children: isError ? (_jsx(EmptyState, { buttonProps: {
                children: 'Try again',
                onClick: refetchSchedules,
            }, description: "Something went wrong while fetching the data. Please try again.", icon: "exclamation", iconColor: "red.500", title: "Failed to load data" })) : (_jsx(Spinner, {})) })));
};
